import { useFormContext } from 'react-hook-form';
import { Typography, Box, Paper, Grid, IconButton, Tooltip, Badge } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import React, { useState, memo } from 'react';
import { useSelector } from 'react-redux';
import Queue from 'queue';
import CircularProgress from '@mui/material/CircularProgress';
import { enqueueSnackbar } from '../../../../../reusable-components/snackbar';
import Iconify from '../../../../../reusable-components/iconify';
import { openFile, saveFile, deleteFile } from '../../../../../api/transitions';
import FileThumbnail from '../../../../../reusable-components/file-thumbnail/FileThumbnail';

FileDrop.propTypes = {
  fieldAttributes: PropTypes.object,
  onFileRemoved: PropTypes.func,
  onFileAdded: PropTypes.func,
  files: PropTypes.array,
};

export default function FileDrop({ fieldAttributes, onFileRemoved, onFileAdded, files }) {
  const [index, setIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const isCanceled = useSelector((state) => state.transitions?.data?.isCanceled);
  const disabled = isCanceled || !fieldAttributes.isEditable;
  const { getValues } = useFormContext();
  const fileQueue = new Queue({ concurrency: 1, autostart: true, results: [] });

  const goToNext = () => {
    setIndex((prev) => prev + 1);
  };

  const goToPrev = () => {
    setIndex((prev) => prev - 1);
  };

  const onDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDropRejected = (files) => {
    files.forEach((file) => {
      let message = '';
      if (file.errors[0]?.code === 'file-invalid-type') message = 'Incorrect file type';
      if (file.errors[0]?.code === 'file-too-large') message = 'File too large';

      enqueueSnackbar(`${message}: Error uploading ${file.file.name}`, { variant: 'error' });
    });
  };

  const onDropAccepted = (files) => {
    setUploading(true);

    files.forEach((file) => {
      const formData = {
        id: 0,
        fileTypeId: fieldAttributes.fileTypeId,
        fileName: file.name,
        deleteFlag: false,
        physicalFile: file,
        transitionFacilityId: getValues().transitionFacilityId,
      };

      fileQueue.push(
        () =>
          new Promise((resolve) => {
            saveFile(formData, 0)
              .then((res) => {
                resolve({ ...res.data });
              })
              .catch(() => {
                setUploading(false);
                enqueueSnackbar(`Error Saving file: ${file.name}`, { variant: 'error' });
                resolve();
              });
          })
      );
    });
  };

  fileQueue.addEventListener('end', () => {
    setUploading(false);
    onFileAdded(fileQueue.results?.filter((res) => res[0] !== undefined));
  });

  const removeFile = (file) => {
    deleteFile(file.id)
      .then((res) => {
        onFileRemoved(res.data);
      })
      .catch(() => {
        enqueueSnackbar(`Error deleting file: ${file.fileName}`, { variant: 'error' });
      });
  };

  const viewFile = (file) => {
    if (file.tempId) {
      window.open(URL.createObjectURL(file.physicalFile), '_blank');
    } else {
      openFile(file.id)
        .then((res) => {
          const blob = new Blob([res.data], { type: res.data.type });
          window.open(URL.createObjectURL(blob), '_blank');
        })
        .catch(() => {
          enqueueSnackbar(`Error opening file: ${file.fileName}`, { variant: 'error' });
        });
    }
  };

  const {
    getRootProps,
    getInputProps,
    open: openDropzone,
    isDragActive,
  } = useDropzone({
    onDropAccepted,
    onDropRejected,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    disabled,
    accept: fieldAttributes.acceptedFileTypes || undefined,
    maxSize: 20971520, // 20 MB
  });

  const FileDropText = () => {
    const uploadIcon = (
      <Grid item sx={{ display: 'flex', mt: 'auto' }}>
        <Iconify icon="mdi:tray-arrow-up" clickable={false} />
      </Grid>
    );

    if (uploading) {
      return (
        <Grid item container alignItems="center" justifyContent="center" spacing={1} sx={{ pl: '4px' }}>
          <Grid item>
            <Typography variant="subtitle2" color="#808080eb">
              Uploading File(s)
            </Typography>
          </Grid>
          <Grid item>
            <CircularProgress size={11} />
          </Grid>
        </Grid>
      );
    }

    if (disabled) {
      return (
        <>
          {uploadIcon}
          <Grid item sx={{ pl: '4px' }}>
            <Typography variant="caption" textAlign="center">
              File drop not permitted
            </Typography>
          </Grid>
        </>
      );
    }

    return (
      <>
        {uploadIcon}
        <Grid item sx={{ pl: '4px' }}>
          <Typography variant="caption" textAlign="center">
            Drop or{' '}
            <Typography
              component="span"
              onClick={!disabled ? openDropzone : undefined}
              variant="caption"
              sx={{ color: 'info.main', textDecoration: 'underline', cursor: 'pointer' }}
            >
              click
            </Typography>{' '}
            to upload
          </Typography>
        </Grid>
      </>
    );
  };

  const FileBox = memo(({ file }) => {
    return (
      <Grid
        item
        sx={{
          flex: '1 1 50%',
          maxWidth: '50%',
          borderRadius: '5px',
          backgroundColor: 'rgba(193, 201, 208, 0.2)',
          p: 0.5,
        }}
        key={file.tempId ?? file.id}
      >
        <Grid container direction="row" wrap="nowrap">
          <Grid item onClick={() => viewFile(file)}>
            <FileThumbnail file={{ name: file.fileName }} tooltip sx={{ width: 20, height: 20 }} />
          </Grid>
          <Grid item xs zeroMinWidth>
            <Tooltip title={file.fileName}>
              <Typography
                onClick={() => viewFile(file)}
                sx={{
                  px: 0,
                  color: (theme) => theme.palette.grey[700],
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    fontWeight: 'bold',
                  },
                }}
                variant="body2"
              >
                {file.fileName}
              </Typography>
            </Tooltip>
          </Grid>
          {!disabled && (
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => removeFile(file)} sx={{ p: 0 }}>
                <Iconify icon="mdi:close" sx={{ mr: 0, width: '20px', height: '20px' }} />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  });

  const fileLength = files.length;

  const visibleFiles = files.slice(index, index + 2);

  const canGoPrev = index > 0;
  const canGoNext = index < fileLength - 2;

  const hiddenLeft = index;
  const hiddenRight = Math.max(0, fileLength - (index + 2));

  return (
    <Box {...getRootProps()} sx={{ cursor: 'pointer' }} onDragEnter={onDrag} onDragOver={onDrag} onDragLeave={onDrag}>
      <input {...getInputProps()} />

      <Paper
        elevation={isDragActive ? 6 : 1}
        sx={{
          py: 0,
          px: 1,
          mt: 1,
          alignContent: 'center',
          minHeight: '76px',
          bgcolor: 'background.paper',
          color: 'grey',
          borderColor: 'info.main',
          border: '1px dashed',
          cursor: 'default',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            bgcolor: disabled ? 'background.paper' : 'background.default',
            boxShadow: 3,
          },
        }}
      >
        <Grid container justifyContent="center">
          <FileDropText />
        </Grid>

        {fileLength > 0 && (
          <Grid container alignItems="flex-end">
            <Grid item xs={1}>
              <Badge
                badgeContent={hiddenLeft}
                color="transparent"
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                  '& .MuiBadge-badge': {
                    top: 6,
                    left: 6,
                    color: 'info.main',
                    fontSize: '0.75rem',
                  },
                }}
              >
                <IconButton onClick={goToPrev} disabled={!canGoPrev} sx={{ px: 0 }}>
                  <ChevronLeft />
                </IconButton>
              </Badge>
            </Grid>

            <Grid item xs={10}>
              <Grid container wrap="nowrap" sx={{ gap: 1, display: 'flex' }}>
                {visibleFiles.map((file) => (
                  <FileBox key={file.tempId ?? file.id} file={file} />
                ))}
              </Grid>
            </Grid>

            <Grid item xs={1}>
              <Badge
                badgeContent={hiddenRight}
                color="transparent"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    top: 6,
                    right: 6,
                    color: 'info.main',
                    fontSize: '0.75rem',
                  },
                }}
              >
                <IconButton onClick={goToNext} disabled={!canGoNext} sx={{ px: 0 }}>
                  <ChevronRight />
                </IconButton>
              </Badge>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
}
